import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useSignUpApi } from '@hero/api-hooks';
import type { SignUpRequest } from '@hero/api';
import { useNav } from '../navigation/useNav';
import { getReferralAttributionFields } from '../referral/referralStorage';
import { getTempToken, saveSignUpResponse } from './tokens';

export type WizardPersona = SignUpRequest['personType'];
export type WizardStep = 'persona' | 'company' | 'driver' | 'profile';

export type UseSignUpWizardManagerOptions = {
  /** Where to navigate after a successful signup. Defaults to the shared `auth_verified` route. */
  onSuccess?: () => void;
};

/**
 * Unified signup-wizard manager. Owns the cross-step state
 * (`persona` → `roles/employment` → `profile`) and the call to the
 * unified `auth.signUp` endpoint. Identical behavior across
 * customer-dashboard, carrier-dashboard, and admin-panel.
 *
 * The wizard's per-step forms live in their own components; this
 * manager only owns the persona + the final submit so each step can
 * stay small and testable. Each app calls `setPersona`, then
 * `setStep('company' | 'driver' | 'profile')`, then finally
 * `submit(payload)` with the discriminated union member shape.
 *
 * See `docs/relations-v2/08-user-identity.md`.
 */
export function useSignUpWizardManager(options: UseSignUpWizardManagerOptions = {}) {
  const { t } = useTranslation(['toast']);
  const { navigate } = useNav();
  const { onSuccess } = options;

  const [persona, setPersona] = useState<WizardPersona | null>(null);
  const [step, setStep] = useState<WizardStep>('persona');

  const { mutateAsync, isPending } = useSignUpApi();

  useEffect(() => {
    if (!getTempToken()) {
      navigate('auth_register');
    }
  }, [navigate]);

  const submit = useCallback(
    async (payload: SignUpRequest) => {
      try {
        // Referral: the first-touch `hero_r` cookie (shared across
        // `.tryhero.io`, so a landing visit carries into this wizard)
        // and the `hero_vid` visitor id. The backend resolves them to
        // `Org.meta.referral` / `User.meta.referral`; unknown codes are
        // ignored server-side, never rejected.
        const response = await mutateAsync({ ...payload, ...getReferralAttributionFields() });
        saveSignUpResponse({
          access_token: response.access_token,
          refresh_token: response.refresh_token,
        });
        toast.success(t('toast:success.signUp'));
        if (onSuccess) onSuccess();
        else navigate('auth_verified');
        return response;
      } catch (e) {
        const message = e instanceof Error ? e.message : t('toast:error.signUp');
        toast.error(message);
        throw e;
      }
    },
    [mutateAsync, navigate, onSuccess, t],
  );

  const choosePersona = useCallback((next: WizardPersona) => {
    setPersona(next);
    if (next === 'individual_customer') setStep('profile');
    else if (next === 'company') setStep('company');
    else setStep('driver');
  }, []);

  const back = useCallback(() => {
    if (step === 'persona') return;
    if (step === 'profile' && persona === 'individual_customer') setStep('persona');
    else if (step === 'company') setStep('persona');
    else if (step === 'driver') setStep('persona');
    else setStep('persona');
  }, [persona, step]);

  return {
    persona,
    step,
    setStep,
    setPersona,
    choosePersona,
    back,
    submit,
    isPending,
  };
}
